import React from 'react';
import * as LucideIcons from 'lucide-react';
import { HelpCircle, LucideProps } from 'lucide-react';

interface IconProps extends Omit<LucideProps, 'size'> {
    name: string | keyof typeof LucideIcons;
    /**
     * Optional numeric size (pixels). If omitted, prefer CSS sizing via className (w-*, h-*).
     * Passing a numeric value will set the SVG's size explicitly.
     */
    size?: number | undefined;
    color?: string;
    className?: string;
    strokeWidth?: number;
}

function Icon({
    name,
    // default to undefined so CSS classes can control sizing by default
    size,
    color = "currentColor",
    className = "",
    strokeWidth = 2,
    ...props
}: IconProps) {
    const formattedName = typeof name === 'string' ? name.split('-').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join('')
        : name;
    const IconComponent = LucideIcons[formattedName as keyof typeof LucideIcons] as React.ComponentType<LucideProps>;

    if (!IconComponent) {
        // If no icon found, render fallback HelpCircle.
        // Only pass size when explicitly provided so callers can use CSS classes for responsive sizing.
        return (
            <HelpCircle
                {...(typeof size === 'number' ? { size } : {})}
                color="gray"
                strokeWidth={strokeWidth}
                className={className}
                {...props}
            />
        );
    }

    // Prefer CSS sizing via className for responsive, mobile-first design
    // Only pass `size` prop when the caller explicitly provided a numeric size.
    const sizeProp = typeof size === 'number' ? { size } : {};

    return (
        <IconComponent
            {...sizeProp}
            color={color}
            strokeWidth={strokeWidth}
            className={className}
            {...props}
        />
    );
}

export default Icon;